import { useEffect, useMemo, useState } from "react";
import './App.css';
import '@iq/models/styles.css'
import './index.css';
import useUserStore from "./states/user";
import useOrderStore from "./states/order";
import useCustomerStore from "./states/customers";
import useInventoryStore from "./states/inventory";
import { filterAppCatalog, NavBar, PageHeader } from '@iq/models'
import { AiOutlineShop } from "react-icons/ai";
import { IoReceiptOutline } from "react-icons/io5";
import { FiPackage, FiUsers } from "react-icons/fi";
import useIsSmallScreen from "./hooks/useIsSmallScreen.js"
import Catalog from "./pages/Catalog"
import VariableProductPage from "./pages/variableProductPage/[id].jsx"
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { fetchOrders } from "./services/OrderService";
import Orders from "./pages/Orders"
import Customers from "./pages/Customers"
import { fetchCustomers } from "./services/customerService";
import { fetchInventory } from "./services/inventoryService";
import Inventory from "./pages/Inventory"
import { APP_CATALOG, DEFAULT_ENABLED_APP_IDS } from "./navigation/appCatalog";

const ERP_URL =
  process.env.REACT_APP_ERP_URL || "https://localhost:3009";
const AUTH_SESSION_URL =
  process.env.REACT_APP_AUTH_SESSION_URL || "http://localhost:8086/auth/session";
const AUTH_LOGOUT_URL =
  process.env.REACT_APP_AUTH_LOGOUT_URL || "http://localhost:8086/auth/logout";
const UNAUTH_REDIRECT_URL =
  process.env.REACT_APP_UNAUTH_REDIRECT_URL || ERP_URL;
const LOGIN_URL = `${UNAUTH_REDIRECT_URL.replace(/\/$/, "")}/login-client`;
const APP_SUITE_DASHBOARD_URL =
  process.env.REACT_APP_APP_SUITE_DASHBOARD_URL || ERP_URL;

const HOME_LINK = { href: APP_SUITE_DASHBOARD_URL };
const CURRENT_APP = APP_CATALOG.find((app) => app.id === "store");
const PAGE_LINKS = [
  { id: "catalog", label: "Catalog", to: "/catalog", icon: <AiOutlineShop size={24} /> },
  { id: "orders", label: "Orders", to: "/orders", icon: <IoReceiptOutline size={24} /> },
  { id: "inventory", label: "Inventory", to: "/inventory", icon: <FiPackage size={24} /> },
  { id: "customers", label: "Customers", to: "/customers", icon: <FiUsers size={24} /> },
];

function App({ enabledAppIds = DEFAULT_ENABLED_APP_IDS }) {
  const [checkingSession, setCheckingSession] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useIsSmallScreen();
  const session = useUserStore((state) => state.session);
  const userId = session?.profile?.id;
  const userName = [session?.profile?.first_name, session?.profile?.last_name].filter(Boolean).join(" ");
  const setSession = useUserStore((state) => state.setSession);
  const setOrders = useOrderStore((state) => state.setOrders);
  const clearUser = useUserStore((state) => state.clearUser);
  const pathname = location.pathname.toLowerCase();
  const activeId = pathname.startsWith("/orders") ? "orders"
    : pathname.startsWith("/inventory") ? "inventory"
    : pathname.startsWith("/customers") ? "customers" : "catalog";
  const activePage = PAGE_LINKS.find((item) => item.id === activeId).label;
  const displayName = userName || `User ${userId}`;
  const isVariableProductRoute = location.pathname.startsWith("/variableProductPage/");
  const setCustomers = useCustomerStore((state) => state.setCustomers);
  const setInventory = useInventoryStore((state) => state.setInventory);



  async function handleLogout() {
    try {
      await fetch(AUTH_LOGOUT_URL, {
        method: "POST",
        credentials: "include",
      });
    } finally {
      clearUser();
      window.location.replace(LOGIN_URL);
    }
  }

  const permittedApps = session?.permitted_apps;
  const appLinks = useMemo(() => filterAppCatalog(
    filterAppCatalog(APP_CATALOG, enabledAppIds),
    permittedApps ?? []
  ), [enabledAppIds, permittedApps]);

  useEffect(() => {
    let isActive = true;

    async function validateSession() {
      try {
        const response = await fetch(AUTH_SESSION_URL, {
          method: "GET",
          credentials: "include",
        });

        if (!response.ok) {
          clearUser();
          window.location.replace(LOGIN_URL);
          return;
        }

        const payload = await response.json();
        if (isActive) {
          setSession(payload);
          setCheckingSession(false);
        }
      } catch (error) {
        console.error("Session check failed:", error);
        clearUser();
        window.location.replace(LOGIN_URL);
      }
    }

    validateSession();

    return () => {
      isActive = false;
    };
  }, [clearUser, setSession]);

  useEffect(() => {
    if (checkingSession || !session) return;
    async function loadOrders() {
      try {
        const payload = await fetchOrders();
        setOrders(payload?.orders || []);
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      }
    }

    loadOrders();
  }, [checkingSession, session, setOrders]);


  useEffect(() => {
    if (checkingSession || !session) return;
    async function loadCustomers() {
      try {
        const payload = await fetchCustomers();
        setCustomers(payload?.customers || []);
      } catch (error) {
        console.error("Failed to fetch customers:", error);
      }
    }

    loadCustomers();
  }, [checkingSession, session, setCustomers]);

  useEffect(() => {
    if (checkingSession || !session) return;
    async function loadInventory() {
      try {
        const payload = await fetchInventory();
        console.log("inventory payload", payload);
        setInventory(payload?.inventory || []);
      } catch (error) {
        console.error("Failed to fetch inventory:", error);
      }
    }

    loadInventory();
  }, [checkingSession, session, setInventory]);

  if (checkingSession || !session) {
    return
  }

  return (
    <div className={`crm-layout app${isSmallScreen ? " crm-layout-mobile" : ""}`}>
      {!isSmallScreen && (
        <div className="nav-section">
              <NavBar
                activeId={activeId}
                navItems={PAGE_LINKS}
                LinkComponent={Link}
                homeLink={HOME_LINK}
              />
        </div>
      )}
      {!isSmallScreen && (
        <div className="page-logo-container">
          {CURRENT_APP?.image && <CURRENT_APP.image className="nav-logo"/>}
        </div>
      )}
      <PageHeader
        title={isVariableProductRoute ? "Product details" : activePage}
        apps={appLinks}
        navItems={PAGE_LINKS}
        activeId={activeId}
        activeAppId="store"
        LinkComponent={Link}
        homeLink={HOME_LINK}
        isSmallScreen={isSmallScreen}
        user={{ name: displayName }}
        logoutAction={{ onClick: handleLogout }}
        back={isVariableProductRoute}
        onBack={() => {
          if (window.history.length > 1) {
            navigate(-1);
            return;
          }

          navigate("/catalog");
        }}
        userMenu={{ name: displayName, onLogout: handleLogout }}
      />
      <Routes>
        <Route path="/" element={<Navigate to="/catalog" replace />} />
        <Route path="/catalog" element={<Catalog onOpenProduct={(productId) => navigate(`/variableProductPage/${productId}`)} />}/>
        <Route path="/orders" element={<Orders />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/variableProductPage/:id" element={<VariableProductPage />} />
        <Route path="*" element={<Navigate to="/catalog" replace />} />
      </Routes>
    </div>
  );
}

export default App;
